// All city names remain visible together. The callouts move the labels, never
// the geographic pins: AJK and the twin cities are too close for names to sit
// directly beside every point without colliding.
//
// Coordinates share pakistanMap.ts's original 720-unit projection. The wider
// view boxes add breathing room around that geometry without transforming it.
// Use dominantBaseline="middle" for text and terminate each leader eight units
// before its label: x - 8 for a start anchor, x + 8 for an end anchor.

export type ReachCityLabel = {
  x: number
  y: number
  anchor: 'start' | 'end'
  elbowX: number
  elbowY: number
}

export const DESKTOP_REACH_VIEWBOX = '-140 0 1000 720'
export const MOBILE_REACH_VIEWBOX = '-270 -20 1260 760'

export const CLIENT_REACH_LABELS: Record<string, ReachCityLabel> = {
  Karimabad: { x: 680, y: 72, anchor: 'start', elbowX: 626, elbowY: 72 },
  Gilgit: { x: 680, y: 112, anchor: 'start', elbowX: 633, elbowY: 112 },
  Chilas: { x: 435, y: 108, anchor: 'end', elbowX: 485, elbowY: 108 },
  Skardu: { x: 680, y: 151, anchor: 'start', elbowX: 650, elbowY: 151 },
  Mingora: { x: 360, y: 146, anchor: 'end', elbowX: 418, elbowY: 146 },
  Mardan: { x: 360, y: 183, anchor: 'end', elbowX: 413, elbowY: 183 },
  Peshawar: { x: 360, y: 220, anchor: 'end', elbowX: 403, elbowY: 220 },
  Abbottabad: { x: 360, y: 257, anchor: 'end', elbowX: 428, elbowY: 257 },
  Islamabad: { x: 360, y: 296, anchor: 'end', elbowX: 431, elbowY: 296 },
  Rawalpindi: { x: 360, y: 335, anchor: 'end', elbowX: 440, elbowY: 335 },
  Muzaffarabad: { x: 680, y: 190, anchor: 'start', elbowX: 637, elbowY: 190 },
  Rawalakot: { x: 680, y: 227, anchor: 'start', elbowX: 638, elbowY: 227 },
  Kotli: { x: 680, y: 264, anchor: 'start', elbowX: 638, elbowY: 264 },
  Mirpur: { x: 680, y: 301, anchor: 'start', elbowX: 638, elbowY: 301 },
  Lahore: { x: 650, y: 340, anchor: 'start', elbowX: 626, elbowY: 340 },
  Faisalabad: { x: 595, y: 377, anchor: 'start', elbowX: 571, elbowY: 377 },
  Multan: { x: 500, y: 415, anchor: 'start', elbowX: 476, elbowY: 415 },
  Quetta: { x: 240, y: 363, anchor: 'end', elbowX: 256, elbowY: 363 },
  Khuzdar: { x: 225, y: 465, anchor: 'end', elbowX: 241, elbowY: 465 },
  Sukkur: { x: 390, y: 469, anchor: 'start', elbowX: 366, elbowY: 469 },
  Larkana: { x: 341, y: 526, anchor: 'start', elbowX: 328, elbowY: 526 },
  Turbat: { x: 91, y: 538, anchor: 'end', elbowX: 107, elbowY: 538 },
  Gwadar: { x: 67, y: 622, anchor: 'end', elbowX: 83, elbowY: 622 },
  Hyderabad: { x: 370, y: 590, anchor: 'start', elbowX: 346, elbowY: 590 },
  Karachi: { x: 291, y: 644, anchor: 'start', elbowX: 278, elbowY: 644 },
}

// On a narrow screen the labels occupy two outside banks, in geographic
// north-to-south order. Their 54-unit spacing leaves room for a 32-unit font
// while keeping all 25 names visible in one view, without a scrolling map.
export const MOBILE_CLIENT_REACH_LABELS: Record<string, ReachCityLabel> = {
  Mingora: { x: -20, y: 54, anchor: 'end', elbowX: 50, elbowY: 54 },
  Mardan: { x: -20, y: 108, anchor: 'end', elbowX: 50, elbowY: 108 },
  Abbottabad: { x: -20, y: 162, anchor: 'end', elbowX: 50, elbowY: 162 },
  Peshawar: { x: -20, y: 216, anchor: 'end', elbowX: 50, elbowY: 216 },
  Quetta: { x: -20, y: 270, anchor: 'end', elbowX: 50, elbowY: 270 },
  Khuzdar: { x: -20, y: 324, anchor: 'end', elbowX: 50, elbowY: 324 },
  Sukkur: { x: -20, y: 378, anchor: 'end', elbowX: 50, elbowY: 378 },
  Larkana: { x: -20, y: 432, anchor: 'end', elbowX: 50, elbowY: 432 },
  Turbat: { x: -20, y: 486, anchor: 'end', elbowX: 50, elbowY: 486 },
  Hyderabad: { x: -20, y: 540, anchor: 'end', elbowX: 50, elbowY: 540 },
  Gwadar: { x: -20, y: 594, anchor: 'end', elbowX: 50, elbowY: 594 },
  Karachi: { x: -20, y: 648, anchor: 'end', elbowX: 50, elbowY: 648 },
  Karimabad: { x: 740, y: 40, anchor: 'start', elbowX: 700, elbowY: 40 },
  Gilgit: { x: 740, y: 94, anchor: 'start', elbowX: 700, elbowY: 94 },
  Chilas: { x: 740, y: 148, anchor: 'start', elbowX: 700, elbowY: 148 },
  Skardu: { x: 740, y: 202, anchor: 'start', elbowX: 700, elbowY: 202 },
  Muzaffarabad: { x: 740, y: 256, anchor: 'start', elbowX: 700, elbowY: 256 },
  Rawalakot: { x: 740, y: 310, anchor: 'start', elbowX: 700, elbowY: 310 },
  Islamabad: { x: 740, y: 364, anchor: 'start', elbowX: 700, elbowY: 364 },
  Rawalpindi: { x: 740, y: 418, anchor: 'start', elbowX: 700, elbowY: 418 },
  Kotli: { x: 740, y: 472, anchor: 'start', elbowX: 700, elbowY: 472 },
  Mirpur: { x: 740, y: 526, anchor: 'start', elbowX: 700, elbowY: 526 },
  Lahore: { x: 740, y: 580, anchor: 'start', elbowX: 700, elbowY: 580 },
  Faisalabad: { x: 740, y: 634, anchor: 'start', elbowX: 700, elbowY: 634 },
  Multan: { x: 740, y: 688, anchor: 'start', elbowX: 700, elbowY: 688 },
}
